import type { PayloadSnapshot, Row } from './types'
import { appUnit } from './grouping'

export const CSV_COLUMNS = [
  'key', 'was', 'summary', 'project', 'svp_group', 'svp_lead', 'svp_confidence', 'risk_category',
  'risk_category_state', 'epic_key', 'epic_name', 'type', 'status', 'status_category', 'open',
  'priority', 'risk_score', 'risk_score_state', 'risk_tier', 'duedate', 'due_status', 'days_to_due',
  'due_moves', 'due_slips', 'due_pulled_in', 'due_set', 'due_cleared', 'due_net_slip_days',
  'due_last_move', 'due_first_committed', 'created', 'age_days', 'entered_register', 'entered_estimated',
  'status_changed', 'idle_days', 'idle_estimated', 'assignee', 'manager', 'manager_state',
  'manager_via_team', 'cortex_team', 'cortex_team_retired', 'ownership_source', 'ownership_confidence',
  'apps', 'app_unit', 'assignee_changes', 'resolution', 'delivery_state', 'change_records', 'deployed_on',
  // Renamed from project_changes_year: the aggregate window now defaults to 90 days and the user can
  // set it to 180 or 365, so the count is no longer annual. The window length rides along beside it so
  // an exported file states what the count covers.
  'deployed_days_ago', 'other_releasers', 'project_changes_in_window', 'project_changes_window_days',
  'weak_flag', 'jira_url',
] as const

// A cell that starts (after any leading whitespace) with one of these characters gets
// evaluated as a formula by Excel/Sheets when opened, so it is prefixed with a leading quote
// to force text interpretation. Shared by both the CSV and xlsx export paths.
const FORMULA_PREFIX = /^[\t\r ]*[=+\-@]/

export function neutralizeFormula(text: string): string {
  return FORMULA_PREFIX.test(text) ? `'${text}` : text
}

function escape(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value)
  // Only text can carry a formula, and the leading `-` of a negative number matches FORMULA_PREFIX.
  // Guarding on the source type (as excelCell does) keeps `due_days: -12` numeric in both exports;
  // neutralizing the rendered string here made the CSV disagree with the xlsx on every overdue row.
  const safeText = typeof value === 'string' ? neutralizeFormula(text) : text
  return /[",\r\n]/.test(safeText) ? `"${safeText.replace(/"/g, '""')}"` : safeText
}

export function csvValues(
  rows: Row[],
  payload: Pick<PayloadSnapshot, 'categories' | 'apps_by_project' | 'app_multi' | 'app_none' | 'project_changes' | 'jira' | 'chg_window'>,
): unknown[][] {
  return rows.map((row) => {
    const project = payload.project_changes[row.project] || { chgs: 0, issues: 0 }
    return [
      row.key, row.was, row.summary, row.project, row.svp, row.lead, row.conf,
      row.cat ? payload.categories[row.cat] : '', row.cat_src, row.epic_key, row.epic, row.type, row.status,
      row.cat_key, row.open, row.pri, row.score, row.src, row.tier, row.due, row.due_status, row.due_days,
      row.due_moves, row.due_slips, row.due_pulled, row.due_set, row.due_cleared, row.due_net_slip,
      row.due_last_move, row.due_first, row.created, row.age, row.entered, row.entered_est, row.scc,
      row.idle, row.idle_unknown, row.assignee, row.mgr, row.mgr_src, row.mgr_via, row.team_title,
      row.team_retired, row.own_src, row.own_conf, (payload.apps_by_project[row.project] || []).join('; '),
      appUnit(row, payload), row.asg_moves, row.resolution || '', row.dstate || '', row.chgs.join('; '),
      row.deployed_on || '', row.deployed_days, row.releasers.join('; '), project.chgs, payload.chg_window,
      row.weak, `${payload.jira}${row.key}`,
    ]
  })
}

export function toCsv(rows: Row[], payload: Pick<PayloadSnapshot, 'categories' | 'apps_by_project' | 'app_multi' | 'app_none' | 'project_changes' | 'jira' | 'chg_window'>): string {
  return [
    CSV_COLUMNS.join(','),
    ...csvValues(rows, payload).map((values) => values.map(escape).join(',')),
  ].join('\n')
}
